/**
 * Export utilities for downloading proof packets and charts.
 */

import { ExportFormat } from '../contracts/proof';

const API_BASE = '/api';

export async function downloadProof(
  bridgeId: string,
  format: ExportFormat
): Promise<void> {
  const url = `${API_BASE}/exports/${bridgeId}/proof?format=${format}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }

  const blob = await response.blob();
  const filename = getFilenameFromResponse(response) || `proof_${bridgeId}.${format}`;

  downloadBlob(blob, filename);
}

function getFilenameFromResponse(response: Response): string | null {
  const disposition = response.headers.get('Content-Disposition');
  if (!disposition) return null;

  const match = disposition.match(/filename=([^;]+)/);
  return match ? match[1].replace(/"/g, '') : null;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportChartAsPng(
  canvas: HTMLCanvasElement,
  filename: string
): void {
  const dataUrl = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename.endsWith('.png') ? filename : `${filename}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportChartAsSvg(
  svgElement: SVGElement,
  filename: string
): void {
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElement);
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  downloadBlob(blob, filename.endsWith('.svg') ? filename : `${filename}.svg`);
}

export function exportDataAsCsv(
  data: Record<string, unknown>[],
  filename: string
): void {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((header) => {
        const value = row[header];
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return String(value ?? '');
      }).join(',')
    ),
  ];

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  downloadBlob(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}

export const EXPORT_FORMATS = [
  { id: ExportFormat.JSON, label: 'JSON', icon: 'file-json' },
  { id: ExportFormat.CSV, label: 'CSV', icon: 'file-spreadsheet' },
  { id: ExportFormat.PDF, label: 'PDF', icon: 'file-text' },
  { id: ExportFormat.PNG, label: 'PNG', icon: 'image' },
  { id: ExportFormat.SVG, label: 'SVG', icon: 'image' },
];
